import {
  addDays,
  FORMAT_LABELS,
  isClosed,
  SOURCE_LABELS,
  STAGES,
  stageTitle,
  WAIT_AFTER,
  type Stage,
} from '@jt/core';
import type { ApplicationFull } from '@jt/shared';
import { useEffect, useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import { useAppActions, useApplication, useTodayDate, type AppPatch } from '../api/hooks';
import { salaryText } from '../components/AppCard';
import { AreaField, opts, SelectField, TextField, TimeField } from '../components/fields';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon, Logo, safeUrl, STAGE_ICON } from '../components/ui';
import { AI_OFF_HINT, useAi, useOpenDraft } from '../drafts/useDraft';
import { useOpenPrep } from '../prep/PrepSheet';
import { Assistant } from './Assistant';
import { ContactsSection } from './Contacts';
import { FilesTab } from './Files';
import { HistoryTab } from './History';
import { OfferTab } from './OfferTab';

type Tab = 'hist' | 'notes' | 'files' | 'offer';

export function Panel({ id }: { id: string }) {
  const q = useApplication(id);
  const ui = useUI();
  useEffect(() => {
    if (q.error instanceof ApiError && q.error.status === 404) {
      ui.toast('Вакансия не найдена');
      ui.closePanel();
    }
  }, [q.error, ui]);
  useEffect(() => {
    document.getElementById('panel')?.scrollTo(0, 0);
  }, [id]);
  return (
    <>
      <div className="ph">
        <button className="icon-btn" onClick={ui.closePanel} aria-label="Закрыть">
          <Icon name="x" size={20} />
        </button>
      </div>
      <div className="pbody">{q.data ? <PanelBody a={q.data} /> : <p className="muted">Загружаю…</p>}</div>
    </>
  );
}

function PanelBody({ a }: { a: ApplicationFull }) {
  const act = useAppActions();
  const ui = useUI();
  const ai = useAi();
  const openDraft = useOpenDraft();
  const openPrep = useOpenPrep();
  const today = useTodayDate();
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState<Tab>('hist');
  const save = (fields: AppPatch) => act.patch(a.id, fields);

  const showOffer = a.stage === 'offer' || !!(a.offer && (a.offer.salaryK || a.offer.deadline));
  const tabs: [Tab, string][] = [
    ['hist', 'История'],
    ['notes', 'Заметки'],
    ['files', 'Файлы'],
    ...(showOffer ? [['offer', 'Оффер'] as [Tab, string]] : []),
  ];
  const curTab = tabs.some((t) => t[0] === tab) ? tab : 'hist';
  const meta = [salaryText(a), a.format ? FORMAT_LABELS[a.format] : '', a.location]
    .filter(Boolean)
    .join(' · ');

  if (a.deletedAt)
    return (
      <>
        <div className="pco">
          <Logo name={a.company} size={40} />
          <span>{a.company || 'Без названия'}</span>
        </div>
        <h2 className="prole">{a.role || 'Роль не указана'}</h2>
        <p className="pmeta">Вакансия в корзине — через 30 дней удалится окончательно.</p>
        <button
          className="btn primary"
          onClick={() => act.restore(a.id).then(() => ui.toast('Вакансия восстановлена'))}
        >
          <Icon name="restore" /> Восстановить
        </button>
      </>
    );

  return (
    <>
      <div className="pco">
        <Logo name={a.company} size={40} />
        <span>{a.company || 'Без названия'}</span>
      </div>
      <h2 className="prole">{a.role || 'Роль не указана'}</h2>
      {meta && <p className="pmeta">{meta}</p>}
      <div className="pstage">
        <label className="stagesel" style={{ '--c': `var(--s-${a.stage})` } as CSSProperties}>
          <Icon name={STAGE_ICON[a.stage]} size={17} />
          <select
            aria-label="Статус"
            value={a.stage}
            onChange={(e) =>
              act
                .stage(a.id, e.target.value as Stage)
                .catch(() => ui.toast('Статус не сохранился. Повторите.'))
            }
          >
            {STAGES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.title}
              </option>
            ))}
          </select>
          <Icon name="chevD" size={14} />
        </label>
        {WAIT_AFTER[a.stage] && (
          <button
            className="btn sm"
            title={`Статус «${stageTitle(WAIT_AFTER[a.stage]!)}»`}
            onClick={() =>
              act
                .stage(a.id, WAIT_AFTER[a.stage]!)
                .then(() => ui.toast(stageTitle(WAIT_AFTER[a.stage]!)))
                .catch(() => ui.toast('Статус не сохранился. Повторите.'))
            }
          >
            <Icon name="check" size={15} /> Этап пройден — ждём итогов
          </button>
        )}
        <button className={`btn sm ${edit ? 'primary' : 'ghost'}`} onClick={() => setEdit(!edit)}>
          <Icon name={edit ? 'check' : 'pencil'} size={15} /> {edit ? 'Готово' : 'Редактировать'}
        </button>
      </div>
      {a.url && (
        <a className="plink" href={safeUrl(a.url)} target="_blank" rel="noopener noreferrer">
          <Icon name="ext" size={17} /> Вакансия{a.source ? ` в ${SOURCE_LABELS[a.source]}` : ''}
        </a>
      )}
      {edit && (
        <div className="pform">
          <div className="grid">
            <TextField
              label="Компания"
              name="company"
              value={a.company}
              onSave={(v) => save({ company: v ?? '' })}
            />
            <TextField label="Роль" name="role" value={a.role} onSave={(v) => save({ role: v ?? '' })} />
            <TextField
              label="Зарплата / вилка"
              name="salaryRange"
              value={a.salaryRange}
              placeholder="€60–70k / год"
              onSave={(v) => save({ salaryRange: v ?? '' })}
            />
            <SelectField
              label="Формат"
              name="format"
              value={a.format}
              options={opts(FORMAT_LABELS, '—')}
              onSave={(v) => save({ format: v })}
            />
            <TextField
              label="Город / страна"
              name="location"
              value={a.location}
              onSave={(v) => save({ location: v ?? '' })}
            />
            <SelectField
              label="Источник"
              name="source"
              value={a.source}
              options={opts(SOURCE_LABELS, '—')}
              onSave={(v) => save({ source: v })}
            />
            <TextField
              label="Ссылка на вакансию"
              name="url"
              className="full"
              type="url"
              inputMode="url"
              value={a.url}
              onSave={(v) => save({ url: v ?? '' })}
            />
            <TextField
              label="Дата отклика"
              name="appliedAt"
              type="date"
              nullable
              value={a.appliedAt}
              onSave={(v) => save({ appliedAt: v })}
            />
          </div>
        </div>
      )}
      {a.stage === 'rejected' && (
        <div className="grid" style={{ marginTop: 8 }}>
          <AreaField
            label="Причина отказа"
            name="rejectionReason"
            rows={2}
            value={a.rejectionReason}
            placeholder="Если назвали — пригодится для аналитики"
            onSave={(v) => save({ rejectionReason: v })}
          />
        </div>
      )}
      {!isClosed(a.stage) && (
        <section className="psec">
          <div className="psh">
            <h3>Следующий шаг</h3>
            {(a.nextStep || a.nextDate) && (
              <button
                className="btn sm ghost"
                onClick={() =>
                  act
                    .stepDone(a.id)
                    .then(() => ui.toast('Отмечено. Назначьте следующий шаг, когда он появится.'))
                }
              >
                <Icon name="check" size={15} /> Сделано
              </button>
            )}
          </div>
          <div className="grid keep2">
            <TextField
              label="Что"
              name="nextStep"
              className="full"
              value={a.nextStep}
              placeholder="Например: техническое интервью, 60 минут"
              onSave={(v) => save({ nextStep: v ?? '' })}
            />
            <TextField
              label="Когда"
              name="nextDate"
              type="date"
              nullable
              value={a.nextDate}
              onSave={(v) => save({ nextDate: v })}
            />
            <TimeField
              label="Время"
              name="nextTime"
              value={a.nextTime}
              onSave={(v) => save({ nextTime: v })}
            />
          </div>
          <div className="btnrow" style={{ marginTop: 8 }}>
            {(
              [
                [1, 'Завтра'],
                [3, '+3 дня'],
                [7, '+неделя'],
              ] as const
            ).map(([n, t]) => (
              <button key={n} className="btn sm ghost" onClick={() => save({ nextDate: addDays(today, n) })}>
                {t}
              </button>
            ))}
          </div>
        </section>
      )}
      <ContactsSection a={a} />
      <div className="ptabs" role="tablist">
        {tabs.map(([k, t]) => (
          <button key={k} role="tab" aria-selected={curTab === k} onClick={() => setTab(k)}>
            {t}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {curTab === 'hist' && <HistoryTab a={a} />}
        {curTab === 'notes' && (
          <div className="grid">
            <AreaField
              label="Заметки"
              name="notes"
              rows={8}
              value={a.notes}
              placeholder="Стек, впечатления, вопросы к следующему этапу"
              onSave={(v) => save({ notes: v })}
            />
          </div>
        )}
        {curTab === 'files' && <FilesTab a={a} />}
        {curTab === 'offer' && <OfferTab a={a} />}
      </div>
      <Assistant a={a} />
      <button className="btn outline wide" disabled={!ai} onClick={() => openPrep(a.id)}>
        <Icon name="sparkles" /> Подготовить отклик
      </button>
      <button
        className="btn outline wide"
        disabled={!ai}
        onClick={() => openDraft(a.id, a.needsReply ? 'reply' : 'followup')}
      >
        <Icon name="send" /> Создать черновик ответа
      </button>
      <p className="pnote">{ai ? 'Черновик открывается для правки — отправляете вы сами' : AI_OFF_HINT}</p>
      <ConfirmButton
        className="linkbtn danger"
        confirmText="Нажмите ещё раз, чтобы удалить"
        onConfirm={() =>
          act.remove(a.id).then(() => {
            ui.toast('Вакансия в корзине — восстановить можно в «Настройках» → «Аккаунт»');
            ui.closePanel();
          })
        }
      >
        Удалить вакансию
      </ConfirmButton>
    </>
  );
}
